// The console's navigation table — one source for the Sidebar and for the
// Playwright role-matrix / navigation specs (ADR-043 G-5). Keep this file
// free of React/MUI imports so the e2e runner can import it directly; the
// Sidebar maps `icon` to a component.
//
// `roles` mirrors the backend gate on the page's primary API group; a page
// without `roles` is visible to every authenticated user. The backend gate is
// the boundary — this table only decides what the sidebar shows — so the
// permission-boundary spec asserts both: the page redirects away AND the API
// answers 403.
import type { UserRole } from '../types/enums.generated';
import { STORAGE_POOLS_UI } from './features';

export type NavIcon =
  | 'assessment'
  | 'folder'
  | 'history'
  | 'factCheck'
  | 'storage'
  | 'hub'
  | 'schedule'
  | 'restore'
  | 'sensors'
  | 'people'
  | 'groupWork'
  | 'password'
  | 'alert'
  | 'rolePermissions';

type NavGroup = 'backup' | 'storage' | 'access' | 'system';

/** Sidebar section headers, in display order. */
export const NAV_GROUPS: readonly { key: NavGroup; label: string }[] = [
  { key: 'backup', label: '備份作業' },
  { key: 'storage', label: '儲存與來源' },
  { key: 'access', label: '帳號與權限' },
  { key: 'system', label: '系統' },
];

export interface NavItem {
  text: string;
  path: string;
  icon: NavIcon;
  /** Sidebar section; absent = above the first section header (總覽). */
  group?: NavGroup;
  /** Roles that may see and open the page; absent = everyone. */
  roles?: readonly UserRole[];
  /**
   * An API request the backend gates with the same roles, used by the
   * permission-boundary spec to prove the boundary is server-side. Omitted
   * where the backend read is open to every role (ADR-034: watcher reads)
   * and only the management write is gated.
   */
  apiProbe?: { method: 'GET' | 'POST'; path: string };
  /**
   * A page that shows only static content and calls no API (QA #84 角色權限
   * 說明): there is no data behind it to protect server-side, so it carries
   * `roles` without an `apiProbe` and the permission-boundary spec skips it.
   */
  staticContent?: true;
}

const ADMIN_AND_SUB: readonly UserRole[] = ['admin', 'sub_admin'];

export const NAV_ITEMS: readonly NavItem[] = [
  { text: '總覽', path: '/overview', icon: 'assessment' },

  { text: '備份檔案管理', path: '/files', icon: 'folder', group: 'backup' },
  { text: '備份記錄', path: '/logs', icon: 'history', group: 'backup' },
  // backend /api/v1/schedules group is RequireRole("admin","sub_admin") — mirror it here (A-35/F-27)
  {
    text: '自動備份時間', path: '/schedule', icon: 'schedule', group: 'backup', roles: ADMIN_AND_SUB,
    apiProbe: { method: 'GET', path: '/api/v1/schedules' },
  },
  // QA #60 (PM decision 2026-09-17): restore settings are an admin / sub_admin
  // page; backend /api/v1/settings is RequireRole("admin","sub_admin").
  {
    text: '還原目錄設定', path: '/restore', icon: 'restore', group: 'backup', roles: ADMIN_AND_SUB,
    apiProbe: { method: 'GET', path: '/api/v1/settings/restore' },
  },

  {
    text: '備份目的地設定', path: '/backup-destinations', icon: 'storage', group: 'storage', roles: ADMIN_AND_SUB,
    apiProbe: { method: 'GET', path: '/api/v1/backup-destinations' },
  },
  // ADR-050: backend /api/v1/storage-pools is RequireRole("admin","sub_admin")
  // (writes admin only); the switch is in config/features.ts.
  ...(STORAGE_POOLS_UI
    ? [{
      text: '儲存池', path: '/storage-pools', icon: 'hub', group: 'storage', roles: ADMIN_AND_SUB,
      apiProbe: { method: 'GET', path: '/api/v1/storage-pools' },
    } as const]
    : []),
  {
    // ADR-034: watcher reads are open to every role; only management is gated.
    text: 'File Watcher', path: '/file-watchers', icon: 'sensors', group: 'storage', roles: ADMIN_AND_SUB,
    apiProbe: { method: 'POST', path: '/api/v1/watchers' },
  },

  {
    text: '使用者管理', path: '/users', icon: 'people', group: 'access', roles: ADMIN_AND_SUB,
    apiProbe: { method: 'GET', path: '/api/v1/users' },
  },
  {
    text: '群組管理', path: '/groups', icon: 'groupWork', group: 'access', roles: ['admin'],
    apiProbe: { method: 'GET', path: '/api/v1/groups' },
  },
  // QA #84 (PM decision 2026-09-30): read-only explanation of what each role
  // can see and do. It calls no API, so there is no server boundary to probe;
  // the route guard alone keeps it to admins.
  { text: '角色權限說明', path: '/role-permissions', icon: 'rolePermissions', group: 'access', roles: ['admin'], staticContent: true },

  // Every role (SRS FR-105: an operator sees their own log); the backend
  // scopes rows by role, so there is no boundary to probe here (D-10).
  { text: '稽核紀錄', path: '/audit-logs', icon: 'factCheck', group: 'system' },
  // QA #60: alert recipients are an admin / sub_admin page; backend
  // /api/v1/alerts is RequireRole("admin","sub_admin").
  {
    text: '告警設定', path: '/alert-settings', icon: 'alert', group: 'system', roles: ADMIN_AND_SUB,
    apiProbe: { method: 'GET', path: '/api/v1/alerts/configs' },
  },
  { text: '密碼修改', path: '/password', icon: 'password', group: 'system' },
];

/** Items a user with `role` may see (undefined role = not logged in → only ungated items). */
export function visibleNavItems(role?: UserRole): NavItem[] {
  return NAV_ITEMS.filter((item) => !item.roles || (role !== undefined && item.roles.includes(role)));
}

interface NavSection {
  /** Group key from NAV_GROUPS; absent for the items above the first header. */
  key?: string;
  /** Section header; absent for the items above the first header. */
  label?: string;
  items: NavItem[];
}

/**
 * The sidebar for `role`: the ungrouped items, then each group in NAV_GROUPS
 * order. A group with nothing visible for the role is dropped with its header
 * (an operator has no 帳號與權限 section).
 */
export function navSections(role?: UserRole): NavSection[] {
  const visible = visibleNavItems(role);
  const sections: NavSection[] = [
    { items: visible.filter((item) => !item.group) },
    ...NAV_GROUPS.map((g) => ({ key: g.key, label: g.label, items: visible.filter((item) => item.group === g.key) })),
  ];
  return sections.filter((section) => section.items.length > 0);
}

/** Items a user with `role` must NOT be able to open. */
export function forbiddenNavItems(role: UserRole): NavItem[] {
  return NAV_ITEMS.filter((item) => item.roles && !item.roles.includes(role));
}
